import { prepend, map as map$1, sort, isNonEmpty, headNonEmpty, tailNonEmpty, empty as empty$1 } from '../../Chunk/dist/effect-Chunk.esm.js';
import { Tag, get as get$1 } from '../../Context/dist/effect-Context.esm.js';
import { decode, toMillis, seconds, millis } from '../../Duration/dist/effect-Duration.esm.js';
import { equals } from '../../Equal/dist/effect-Equal.esm.js';
import { isDone, isSuspended } from '../../FiberStatus/dist/effect-FiberStatus.esm.js';
import { pipe, identity, constVoid, dual } from '../../Function/dist/effect-Function.esm.js';
import { empty, set as set$1 } from '../../HashMap/dist/effect-HashMap.esm.js';
import { ClockTypeId, clockTag } from '../../internal/clock.esm.js';
import { map, zipRight, flatMap, deferredMake, deferredAwait, deferredSucceed, asUnit, succeed, fail, interruptible, as, interruptFiber, zip, zipWith, yieldNow, unit, fiberRefGetWith, sync } from '../../internal/core.esm.js';
import { reduce, logWarning, delay, sleep as sleep$1, filterOrFail, eventually, gen } from '../../internal/core-effect.esm.js';
import { currentServices } from '../../internal/defaultServices.esm.js';
import { makeSynchronized } from '../../internal/effect/circular.esm.js';
import { zipLeftOptions, fork, withClockScoped, addFinalizer } from '../../internal/fiberRuntime.esm.js';
import { scoped } from '../../internal/layer.esm.js';
import { get, unsafeGet, set, modify, unsafeMake } from '../../internal/ref.esm.js';
import { updateSomeEffect } from '../../internal/synchronizedRef.esm.js';
import { isStart as isStart$1, pending as pending$1, done as done$1, isPending as isPending$1, start } from '../../internal/testing/suspendedWarningData.esm.js';
import { isStart, pending, isPending, done, start as start$1 } from '../../internal/testing/warningData.esm.js';
import { Order } from '../../Number/dist/effect-Number.esm.js';
import { some, none } from '../../Option/dist/effect-Option.esm.js';
import { mapInput } from '../../Order/dist/effect-Order.esm.js';
import { TestAnnotations } from '../../TestAnnotations/dist/effect-TestAnnotations.esm.js';
import { TestLive } from '../../TestLive/dist/effect-TestLive.esm.js';

/**
 * @since 2.0.0
 */

/**
 * A `TestClock` makes it easy to deterministically and efficiently test effects
 * involving the passage of time.
 *
 * Instead of waiting for actual time to pass, `sleep` and methods implemented
 * in terms of it schedule effects to take place at a given clock time. Users
 * can adjust the clock time using the `adjust` and `setTime` methods, and all
 * effects scheduled to take place on or before that time will automatically be
 * run in order.
 *
 * For example, here is how we can test `Effect.timeout` using `TestClock`:
 *
 * ```ts
 * import * as Duration from "effect/Duration"
 * import * as Effect from "effect/Effect"
 * import * as Fiber from "effect/Fiber"
 * import * as TestClock from "effect/TestClock"
 * import * as Option from "effect/Option"
 *
 * Effect.gen(function*() {
 *   const fiber = yield* pipe(
 *     Effect.sleep(Duration.minutes(5)),
 *     Effect.timeout(Duration.minutes(1)),
 *     Effect.fork
 *   )
 *   yield* TestClock.adjust(Duration.minutes(1))
 *   const result = yield* Fiber.join(fiber)
 *   assert.deepStrictEqual(result, Option.none())
 * })
 * ```
 *
 * Note how we forked the fiber that `sleep` was invoked on. Calls to `sleep`
 * and methods derived from it will semantically block until the time is set to
 * on or after the time they are scheduled to run. If we didn't fork the fiber
 * on which we called sleep we would never get to set the time on the line
 * below. Thus, a useful pattern when using `TestClock` is to fork the effect
 * being tested, then adjust the clock time, and finally verify that the
 * expected effects have been performed.
 *
 * @since 2.0.0
 */

/**
 * `Data` represents the state of the `TestClock`, including the clock time.
 *
 * @since 2.0.1
 */

/**
 * @since 2.0.0
 */
const makeData = (instant, sleeps) => ({
  instant,
  sleeps
});

/**
 * @since 2.0.0
 */
const TestClock = /*#__PURE__*/Tag( /*#__PURE__*/Symbol.for("effect/TestClock"));

/**
 * The warning message that will be displayed if a test is using time but is
 * not advancing the `TestClock`.
 *
 * @internal
 */
const warning = "Warning: A test is using time, but is not advancing " + "the test clock, which may result in the test hanging. Use TestClock.adjust to " + "manually advance the time.";

/**
 * The warning message that will be displayed if a test is advancing the clock
 * but a fiber is still running.
 *
 * @internal
 */
const suspendedWarning = "Warning: A test is advancing the test clock, " + "but a fiber is not suspending, which may result in the test hanging. Use " + "TestAspect.diagnose to identity the fiber that is not suspending.";

/** @internal */
class TestClockImpl {
  [ClockTypeId] = ClockTypeId;
  constructor(clockState, live, annotations, warningState, suspendedWarningState) {
    this.clockState = clockState;
    this.live = live;
    this.annotations = annotations;
    this.warningState = warningState;
    this.suspendedWarningState = suspendedWarningState;
    this.currentTimeMillis = map(get(this.clockState), data => data.instant);
    this.currentTimeNanos = map(get(this.clockState), data => BigInt(data.instant * 1000000));
  }

  /**
   * Unsafely returns the current time in milliseconds.
   */
  unsafeCurrentTimeMillis() {
    return unsafeGet(this.clockState).instant;
  }

  /**
   * Unsafely returns the current time in nanoseconds.
   */
  unsafeCurrentTimeNanos() {
    return BigInt(unsafeGet(this.clockState).instant * 1000000);
  }

  /**
   * Returns the current clock time in milliseconds.
   */

  /**
   * Returns the current clock time in nanoseconds.
   */

  /**
   * Saves the `TestClock`'s current state in an effect which, when run, will
   * restore the `TestClock` state to the saved state.
   */
  save() {
    return map(get(this.clockState), data => set(this.clockState, data));
  }
  /**
   * Sets the current clock time to the specified instant. Any effects that
   * were scheduled to occur on or before the new time will be run in order.
   */
  setTime(instant) {
    return zipRight(this.warningDone(), this.run(() => instant));
  }
  /**
   * Semantically blocks the current fiber until the clock time is equal to or
   * greater than the specified duration. Once the clock time is adjusted to
   * on or after the duration, the fiber will automatically be resumed.
   */
  sleep(durationInput) {
    const duration = decode(durationInput);
    return flatMap(deferredMake(), deferred => pipe(modify(this.clockState, data => {
      const end = data.instant + toMillis(duration);
      if (end > data.instant) {
        return [true, makeData(data.instant, pipe(data.sleeps, prepend([end, deferred])))];
      }
      return [false, data];
    }), flatMap(shouldAwait => shouldAwait ? pipe(this.warningStart(), zipRight(deferredAwait(deferred))) : pipe(deferredSucceed(deferred, void 0), asUnit))));
  }
  /**
   * Returns a list of the times at which all queued effects are scheduled to
   * resume.
   */
  sleeps() {
    return map(get(this.clockState), data => map$1(data.sleeps, _ => _[0]));
  }
  /**
   * Increments the current clock time by the specified duration. Any effects
   * that were scheduled to occur on or before the new time will be run in
   * order.
   */
  adjust(durationInput) {
    const duration = decode(durationInput);
    return zipRight(this.warningDone(), this.run(n => n + toMillis(duration)));
  }
  /**
   * Increments the current clock time by the specified duration. Any effects
   * that were scheduled to occur on or before the new time will be run in
   * order.
   */
  adjustWith(durationInput) {
    const duration = decode(durationInput);
    return effect => zipLeftOptions(effect, this.adjust(duration), {
      concurrent: true
    });
  }
  /**
   * Returns a set of all fibers in this test.
   */
  supervisedFibers() {
    return this.annotations.supervisedFibers();
  }
  /**
   * Captures a "snapshot" of the identifier and status of all fibers in this
   * test other than the current fiber. Fails with the `Unit` value if any of
   * these fibers are not done or suspended. Note that because we cannot
   * synchronize on the status of multiple fibers at the same time this
   * snapshot may not be fully consistent.
   */
  freeze() {
    return flatMap(this.supervisedFibers(), fibers => pipe(fibers, reduce(empty(), (map, fiber) => pipe(fiber.status(), flatMap(status => {
      if (isDone(status)) {
        return succeed(set$1(map, fiber.id(), status));
      }
      if (isSuspended(status)) {
        return succeed(set$1(map, fiber.id(), status));
      }
      return fail(void 0);
    })))));
  }
  /**
   * Forks a fiber that will display a warning message if a test is using time
   * but is not advancing the `TestClock`.
   */
  warningStart() {
    return updateSomeEffect(this.warningState, data => isStart(data) ? some(pipe(this.live.provide(pipe(logWarning(warning), delay(seconds(5)))), interruptible, fork, map(fiber => pending(fiber)))) : none());
  }
  /**
   * Cancels the warning message that is displayed if a test is using time but
   * is not advancing the `TestClock`.
   */
  warningDone() {
    return updateSomeEffect(this.warningState, warningData => {
      if (isStart(warningData)) {
        return some(succeed(done));
      }
      if (isPending(warningData)) {
        return some(pipe(interruptFiber(warningData.fiber), as(done)));
      }
      return none();
    });
  }
  /**
   * Returns whether all descendants of this fiber are done or suspended.
   */
  suspended() {
    return pipe(this.freeze(), zip(this.live.provide(pipe(sleep$1(millis(5)), zipRight(this.freeze())))), flatMap(([first, last]) => equals(first, last) ? succeed(first) : fail(void 0)));
  }
  /**
   * Polls until all descendants of this fiber are done or suspended.
   */
  awaitSuspended() {
    return pipe(this.suspendedWarningStart(), zipRight(pipe(this.suspended(), zipWith(pipe(this.live.provide(sleep$1(millis(10))), zipRight(this.suspended())), equals), filterOrFail(identity, constVoid), eventually)), zipRight(this.suspendedWarningDone()));
  }
  /**
   * Forks a fiber that will display a warning message if a test is advancing
   * the `TestClock` but a fiber is not suspending.
   */
  suspendedWarningStart() {
    return updateSomeEffect(this.suspendedWarningState, suspendedWarningData => {
      if (isStart$1(suspendedWarningData)) {
        return some(pipe(this.live.provide(pipe(logWarning(suspendedWarning), zipRight(set(this.suspendedWarningState, done$1)), delay(seconds(5)))), interruptible, fork, map(fiber => pending$1(fiber))));
      }
      return none();
    });
  }
  /**
   * Cancels the warning message that is displayed if a test is advancing the
   * `TestClock` but a fiber is not suspending.
   */
  suspendedWarningDone() {
    return updateSomeEffect(this.suspendedWarningState, suspendedWarningData => {
      if (isPending$1(suspendedWarningData)) {
        return some(pipe(interruptFiber(suspendedWarningData.fiber), as(start)));
      }
      return none();
    });
  }
  /**
   * Runs all effects scheduled to occur on or before the specified instant,
   * which may depend on the current time, in order.
   */
  run(f) {
    return pipe(this.awaitSuspended(), zipRight(pipe(modify(this.clockState, data => {
      const end = f(data.instant);
      const sorted = pipe(data.sleeps, sort(pipe(Order, mapInput(_ => _[0]))));
      if (isNonEmpty(sorted)) {
        const [instant, deferred] = headNonEmpty(sorted);
        if (instant <= end) {
          return [some([end, deferred]), makeData(instant, tailNonEmpty(sorted))];
        }
      }
      return [none(), makeData(end, data.sleeps)];
    }), flatMap(option => {
      switch (option._tag) {
        case "None":
          {
            return unit;
          }
        case "Some":
          {
            const [end, deferred] = option.value;
            return pipe(deferredSucceed(deferred, void 0), zipRight(yieldNow()), zipRight(this.run(() => end)));
          }
      }
    }))));
  }
}

/**
 * @since 2.0.0
 */
const live = data => scoped(TestClock, gen(function* ($) {
  const live = yield* $(TestLive);
  const annotations = yield* $(TestAnnotations);
  const clockState = yield* $(sync(() => unsafeMake(data)));
  const warningState = yield* $(makeSynchronized(start$1));
  const suspendedWarningState = yield* $(makeSynchronized(start));
  const testClock = new TestClockImpl(clockState, live, annotations, warningState, suspendedWarningState);
  yield* $(withClockScoped(testClock));
  yield* $(addFinalizer(() => zipRight(testClock.warningDone(), testClock.suspendedWarningDone())));
  return testClock;
}));

/**
 * @since 2.0.0
 */
const defaultTestClock = /*#__PURE__*/live( /*#__PURE__*/makeData( /*#__PURE__*/new Date(0).getTime(), /*#__PURE__*/empty$1()));

/**
 * Accesses a `TestClock` instance in the context and increments the time
 * by the specified duration, running any actions scheduled for on or before
 * the new time in order.
 *
 * @since 2.0.0
 */
const adjust = durationInput => {
  const duration = decode(durationInput);
  return testClockWith(testClock => testClock.adjust(duration));
};

/**
 * @since 2.0.0
 */
const adjustWith = /*#__PURE__*/dual(2, (effect, durationInput) => {
  const duration = decode(durationInput);
  return testClockWith(testClock => testClock.adjustWith(duration)(effect));
});

/**
 * Accesses a `TestClock` instance in the context and saves the clock
 * state in an effect which, when run, will restore the `TestClock` to the
 * saved state.
 *
 * @since 2.0.0
 */
const save = () => testClockWith(testClock => testClock.save());

/**
 * Accesses a `TestClock` instance in the context and sets the clock time
 * to the specified `Instant`, running any actions scheduled for on or before
 * the new time in order.
 *
 * @since 2.0.0
 */
const setTime = instant => testClockWith(testClock => testClock.setTime(instant));

/**
 * Semantically blocks the current fiber until the clock time is equal to or
 * greater than the specified duration. Once the clock time is adjusted to
 * on or after the duration, the fiber will automatically be resumed.
 *
 * @since 2.0.0
 */
const sleep = durationInput => {
  const duration = decode(durationInput);
  return testClockWith(testClock => testClock.sleep(duration));
};

/**
 * Accesses a `TestClock` instance in the context and returns a list of
 * times that effects are scheduled to run.
 *
 * @since 2.0.0
 */
const sleeps = () => testClockWith(testClock => testClock.sleeps());

/**
 * Retrieves the `TestClock` service for this test.
 *
 * @since 2.0.0
 */
const testClock = () => testClockWith(succeed);

/**
 * Retrieves the `TestClock` service for this test and uses it to run the
 * specified workflow.
 *
 * @since 2.0.0
 */
const testClockWith = f => fiberRefGetWith(currentServices, services => f(pipe(services, get$1(clockTag))));

/**
 * Accesses the current time of a `TestClock` instance in the context in
 * milliseconds.
 *
 * @since 2.0.0
 */
const currentTimeMillis = /*#__PURE__*/testClockWith(testClock => testClock.currentTimeMillis);

export { TestClock, TestClockImpl, adjust, adjustWith, currentTimeMillis, defaultTestClock, live, makeData, save, setTime, sleep, sleeps, testClock, testClockWith };
